import { TrackingService } from '../services/TrackingService'
import { FormStarted } from '../events/FormStarted'
import type { FormContext, StepContext } from '../types'

export interface UseFormStartedProps {
  formContext: FormContext
  stepContext?: StepContext
}

/**
 * Hook for tracking form start events
 *
 * Tracks when a user begins interacting with a form. This is useful for
 * understanding form engagement and calculating form completion rates.
 *
 * @returns A function that tracks form start events
 *
 * @example
 * ```typescript
 * const trackFormStarted = useFormStarted()
 *
 * const handleFormFocus = () => {
 *   trackFormStarted({
 *     formContext: {
 *       formName: 'fmp_v1',
 *       formType: '',
 *       formId: 'form-123'
 *     },
 *     stepContext: {
 *       stepId: '',
 *       stepName: 'Quiz Start',
 *       stepNumber: 0
 *     }
 *   })
 * }
 * ```
 */
const useFormStarted =
  () =>
  (props: UseFormStartedProps): void => {
    const { formContext, stepContext } = props

    if (!formContext) {
      console.error('Missing required props for form start.')
      return
    }

    const formStartedEventPayload = new FormStarted({
      formContext,
      stepContext,
    })

    TrackingService.track(formStartedEventPayload)
  }

export default useFormStarted
